import React, { useCallback, useEffect, useState } from 'react';
import {
  CreditCard,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ExternalLink,
  Mail,
  AlertTriangle,
  UserX,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { money, BRAND, PLAN_CODES, PLAN_PRICES, PLAN_INCLUDED_LEADS, planBillingSuffix } from '@/data/constants';


/**
 * Admin > Payments.
 *
 * Calls the checkout-link edge function ONCE PER PLAN in probe mode so an
 * administrator can see at a glance which hosted checkout links are live
 * (green) and which are missing their secret (red) — without having to click
 * through the public pricing page.
 *
 * The lower table surfaces mkt_checkout_failures: every subcontractor who hit
 * a plan whose checkout link was unavailable, so the team can follow up before
 * the sale is lost.
 */

interface PlanStatus {
  code: string;
  configured: boolean | null; // null = still checking
  url?: string;
  envName?: string;
  message?: string;
  reason?: string;
}

interface FailureRow {
  id: string;
  plan_code: string;
  user_id: string | null;
  email: string | null;
  name: string | null;
  phone: string | null;
  reason: string;
  message: string | null;
  language: string;
  resolved: boolean;
  created_at: string;
}

const planLabel = (code: string) => code.charAt(0).toUpperCase() + code.slice(1);

const AdminPayments: React.FC = () => {
  const { t, lang } = useI18n();
  const [statuses, setStatuses] = useState<PlanStatus[]>(
    PLAN_CODES.map((code) => ({ code, configured: null }))
  );
  const [checking, setChecking] = useState(false);
  const [failures, setFailures] = useState<FailureRow[]>([]);

  /** Probe every plan's checkout link (probe mode never logs a failure row). */
  const runChecks = useCallback(async () => {
    setChecking(true);
    setStatuses(PLAN_CODES.map((code) => ({ code, configured: null })));

    const results = await Promise.all(
      PLAN_CODES.map(async (code): Promise<PlanStatus> => {
        const { data, error } = await supabase.functions.invoke('checkout-link', {
          body: { plan: code, probe: true, language: lang },
        });
        if (error || !data) {
          return { code, configured: false, reason: 'error', message: error?.message || '' };
        }
        return {
          code,
          configured: Boolean(data.configured && data.url),
          url: data.url,
          envName: data.envName || `GHL_${code.toUpperCase()}_CHECKOUT_URL`,
          message: data.message,
          reason: data.reason,
        };
      })
    );

    setStatuses(results);
    setChecking(false);
  }, [lang]);

  const loadFailures = useCallback(async () => {
    const { data } = await supabase
      .from('mkt_checkout_failures')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);
    setFailures((data as FailureRow[]) || []);
  }, []);

  useEffect(() => {
    runChecks();
    loadFailures();
  }, [runChecks, loadFailures]);

  const toggleResolved = async (row: FailureRow) => {
    const next = !row.resolved;
    await supabase.from('mkt_checkout_failures').update({ resolved: next }).eq('id', row.id);
    setFailures((prev) => prev.map((f) => (f.id === row.id ? { ...f, resolved: next } : f)));
  };

  const missing = statuses.filter((s) => s.configured === false).length;
  const reasonLabel = (reason: string) => {
    const key = `admin.payReason.${reason}`;
    const label = t(key);
    return label === key ? reason : label;
  };

  return (
    <div>
      {/* ---------- Plan checkout link status ---------- */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-extrabold text-slate-900">
            <CreditCard className="h-5 w-5 text-orange-500" /> {t('admin.payTitle')}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">{t('admin.paySub')}</p>
        </div>
        <button
          onClick={runChecks}
          disabled={checking}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
          {checking ? t('admin.payChecking') : t('admin.payRecheck')}
        </button>
      </div>

      <div className="mt-5 space-y-3">
        {statuses.map((s) => {
          const ok = s.configured === true;
          const pending = s.configured === null;
          return (
            <div
              key={s.code}
              className={`flex flex-wrap items-center justify-between gap-4 rounded-2xl border p-5 ${
                pending
                  ? 'border-slate-200 bg-white'
                  : ok
                  ? 'border-emerald-200 bg-emerald-50/60'
                  : 'border-red-200 bg-red-50/60'
              }`}
            >
              <div className="flex items-center gap-3">
                {pending ? (
                  <RefreshCw className="h-6 w-6 animate-spin text-slate-300" />
                ) : ok ? (
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600" />
                )}
                <div>
                  <p className="text-base font-extrabold text-slate-900">
                    {planLabel(s.code)}{' '}
                    <span className="text-sm font-semibold text-slate-400">
                      {money(PLAN_PRICES[s.code])}
                      {planBillingSuffix(s.code, lang)} · {PLAN_INCLUDED_LEADS[s.code]} {t('dash.includedLeads')}
                    </span>

                  </p>
                  <p
                    className={`mt-0.5 text-sm font-bold ${
                      pending ? 'text-slate-400' : ok ? 'text-emerald-700' : 'text-red-700'
                    }`}
                  >
                    {pending ? t('admin.payChecking') : ok ? t('admin.payReady') : t('admin.payMissing')}
                  </p>
                  {!pending && !ok && s.message && (
                    <p className="mt-1 max-w-xl text-xs leading-relaxed text-red-700">{s.message}</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end gap-1.5">
                <code className="rounded-lg bg-white px-2.5 py-1 text-[11px] font-bold text-slate-500 ring-1 ring-slate-200">
                  {t('admin.paySecret')}: {s.envName || `GHL_${s.code.toUpperCase()}_CHECKOUT_URL`}
                </code>
                {ok && s.url && (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> {t('admin.payOpenLink')}
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!checking && statuses.every((s) => s.configured !== null) && (
        <p
          className={`mt-4 rounded-xl px-4 py-3 text-sm font-semibold ${
            missing === 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {missing === 0
            ? t('admin.payAllReady')
            : t('admin.paySomeMissing').replace('{n}', String(missing))}
        </p>
      )}

      {/* ---------- Blocked subscription attempts ---------- */}
      <h2 className="mt-12 flex items-center gap-2 text-xl font-extrabold text-slate-900">
        <AlertTriangle className="h-5 w-5 text-amber-500" /> {t('admin.payFailTitle')}
      </h2>
      <p className="mt-1 max-w-2xl text-sm text-slate-500">{t('admin.payFailSub')}</p>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {failures.length === 0 ? (
          <p className="flex items-center justify-center gap-2 p-8 text-center text-sm text-slate-500">
            <UserX className="h-4 w-4 text-slate-300" /> {t('admin.payFailEmpty')}
          </p>
        ) : (
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">{t('admin.payFailPlan')}</th>
                <th className="px-4 py-3">{t('common.name')}</th>
                <th className="px-4 py-3">{t('common.email')}</th>
                <th className="px-4 py-3">{t('admin.payFailReason')}</th>
                <th className="px-4 py-3">{t('nav.language')}</th>
                <th className="px-4 py-3">{t('admin.payFailWhen')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {failures.map((f) => (
                <tr key={f.id} className={f.resolved ? 'opacity-60' : ''}>
                  <td className="px-4 py-3 font-bold text-slate-900">{planLabel(f.plan_code)}</td>
                  <td className="px-4 py-3 text-slate-700">{f.name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{f.email || '—'}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-bold text-red-700">
                      {reasonLabel(f.reason)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-bold uppercase text-slate-500">{f.language}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {new Date(f.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {f.email && (
                        <a
                          href={`mailto:${f.email}?subject=${encodeURIComponent(
                            `${planLabel(f.plan_code)} plan — ${BRAND.name}`
                          )}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                        >
                          <Mail className="h-3.5 w-3.5" /> {t('admin.payFollowUp')}
                        </a>
                      )}
                      <button
                        onClick={() => toggleResolved(f)}
                        className={`rounded-lg px-3 py-1.5 text-xs font-bold ${
                          f.resolved
                            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-slate-900 text-white hover:bg-slate-800'
                        }`}
                      >
                        {f.resolved ? t('admin.payResolved') : t('admin.payResolve')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminPayments;
